import { useEffect, useRef, useState } from "react";
import { File, FilePdf, FileText, FilmStrip, Image as ImageIcon, MusicNotes, SidebarSimple, SquaresFour, ListBullets, Table, UploadSimple, X, PencilSimple, Trash, MagnifyingGlass, Tag as TagIcon, LinkSimple } from "@phosphor-icons/react";

import "./media.css";
import { Page } from "@/ui/Layout/Page";
import { Loading } from "@/ui/Layout/Loading";
import { EmptyState } from "@/ui/Layout/EmptyState";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { TableView } from "@/ui/DataView/TableView";
import { DefaultDataProps } from "@/ui/DataView/types";
import { getSessionToken } from "@/api/auth";
import { MediaTag, createTag, deleteMedia, deleteTag, fileUnder, finishUpload, listMedia, listTags, mediaLink, publicUrlOf, playUrlOf, embedCodeOf, getMedia, retryVideo, playerSettings, savePlayerSettings, PlayerSettings, renameMedia, setPublic, linkHosts, setNamedLink, removeNamedLink, renameTag, sendToStorage, startUpload, tagFile } from "@/api/media";
import { FilePreview, viewerLabel } from "@/ui/Preview/FilePreview";
import { previewKind } from "@/ui/Preview/previewKind";
import { MEDIA_TYPES, MediaFile, MediaType, addedLabel, campaignList, filterFiles, guessType, suggestLinkName, uploadFiles } from "./library";
import { TagPicker } from "./TagPicker";

// Launch Media — the account's own files, private by default (Specs/Launch Media v1 — PRD).
// Filters on the left (collapsible, like Launch Reviews' side panel) · grid or list · preview pane.
// Layout lives in media.css: the app's pre-built Tailwind file only has classes other screens used.

const TYPE_ICON: Record<MediaType, typeof File> = { image: ImageIcon, video: FilmStrip, audio: MusicNotes, pdf: FilePdf, doc: FileText, sheet: Table, other: File };
const TYPE_COLOR: Record<MediaType, string> = { image: "#7c3aed", video: "#db2777", audio: "#0891b2", pdf: "#dc2626", doc: "#2563eb", sheet: "#16a34a", other: "#6b7280" };
const isPublic = (f: MediaFile) => /^https?:\/\//.test(f.url || "");
const showsAsPicture = (f: MediaFile) => f.type === "image" && isPublic(f);

const remember = { get: (k: string, d: string) => { try { return localStorage.getItem(k) || d; } catch (e) { return d; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch (e) { /* private window */ } } };

const TypeTile = ({ type, size = 22 }: { type: MediaType; size?: number }) => {
  const Icon = TYPE_ICON[type];
  return <div className="tile" style={{ color: TYPE_COLOR[type], background: `${TYPE_COLOR[type]}14` }}><Icon size={size} /></div>;
};

// a card's picture: the file itself when the browser can show it, otherwise its type
const Thumb = ({ f, local }: { f: MediaFile; local?: string | null }) => {
  if (local && f.type === "image") return <img src={local} alt="" />;
  if (local && f.type === "video") return <video src={local} muted playsInline preload="metadata" />;
  if (showsAsPicture(f)) return <img src={f.url} alt="" loading="lazy" />;
  if (f.type === "video" && isPublic(f)) return <video src={`${f.url}#t=0.5`} muted playsInline preload="metadata" />;
  return <TypeTile type={f.type} size={34} />;
};

type Pending = { key: string; name: string; type: MediaType; pct: number; local: string | null; error?: string };

// VH-4: how often the tab checks on a video being prepared (tests make it quick)
let videoPollMs = 6000;
export const setVideoPollMs = (ms: number) => { videoPollMs = ms; };

export const Media = ({ name = "Launch Media" }) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [allTags, setAllTags] = useState<MediaTag[]>([]);
  const [tag, setTag] = useState<string | null>(null);
  // a campaign filter can arrive in the address (the dashboard widget's "Open in library")
  const [campaign, setCampaign] = useState<string | null>(() => { try { return new URLSearchParams(window.location.search).get("campaign"); } catch (e) { return null; } });
  const [q, setQ] = useState("");
  const [newTag, setNewTag] = useState("");
  const [editTag, setEditTag] = useState<{ from: string; text: string } | null>(null);
  const [sureTag, setSureTag] = useState<string | null>(null);
  const [type, setType] = useState<MediaType | null>(null);
  const [view, setView] = useState<"grid" | "list">(() => (remember.get("lm-view", "grid") === "list" ? "list" : "grid"));
  const [sideOpen, setSideOpen] = useState(() => remember.get("lm-side", "open") !== "closed");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const picker = useRef<HTMLInputElement>(null);
  // the dashboard widget links straight to a file's preview (?file=<id>)
  const [sel, setSel] = useState<string | null>(() => { try { return new URLSearchParams(window.location.search).get("file"); } catch (e) { return null; } });
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [sure, setSure] = useState(false);
  const alive = useRef(true);
  const token = () => getSessionToken() || "";
  const say = (t: string) => { setNote(t); setTimeout(() => alive.current && setNote(""), 2600); };

  const load = async (quiet = false) => {
    if (!quiet) { setIsLoading(true); setError(""); }
    try {
      const [out, tagList] = await Promise.all([listMedia(token()), listTags(token()).catch(() => null)]);
      if (!alive.current) return;
      setFiles(out.files || []); if (tagList) setAllTags(tagList);
    } catch (e) {
      if (alive.current && !quiet) setError(e instanceof Error ? e.message : "Couldn't load your files.");
    }
    if (alive.current) setIsLoading(false);
  };
  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; }; }, []);

  // uploads: each file shows as a card right away (with its own picture and progress) and becomes
  // a normal file the moment it's in; a failure stays on its card with the reason
  const upload = async (list: FileList | File[] | null) => {
    const chosen = Array.from(list || []).filter((f) => !f.name.startsWith("."));
    if (!chosen.length) return;
    const stamp = Date.now();
    const cards: Pending[] = chosen.map((f, i) => { const t = guessType(f.name, f.type); return { key: `${stamp}-${i}`, name: f.name, type: t, pct: 0, local: t === "image" || t === "video" ? URL.createObjectURL(f) : null }; });
    setPending((p) => [...cards, ...p]);
    const keyOf = (fileName: string) => cards.find((c) => c.name === fileName)?.key;
    const out = await uploadFiles(chosen, {
      start: (f) => startUpload(token(), f),
      send: sendToStorage,
      finish: async (key, fileName) => {
        const f = await finishUpload(token(), key, fileName, campaign); // a campaign filter files new uploads under it
        if (alive.current) { setFiles((all) => [f, ...all.filter((x) => x.id !== f.id)]); setPending((p) => p.filter((c) => c.key !== keyOf(fileName))); }
        return f;
      },
    }, (fileName, pct) => setPending((p) => p.map((c) => (c.key === keyOf(fileName) ? { ...c, pct } : c))));
    if (!alive.current) return;
    setPending((p) => p.map((c) => { const bad = out.failed.find((x) => x.name === c.name && cards.some((k) => k.key === c.key)); return bad ? { ...c, error: bad.error } : c; }));
    for (const c of cards) if (c.local) setTimeout(() => URL.revokeObjectURL(c.local!), 60000);
  };
  // LD-8 — in Launch Desktop, files dragged onto the window (any screen) or the dock icon arrive here
  const uploadRef = useRef(upload);
  uploadRef.current = upload;
  useEffect(() => {
    const desktop = (window as any).launchDesktop;
    if (!desktop || typeof desktop.onFiles !== "function") return;
    return desktop.onFiles((items: { name: string; type: string; data: Uint8Array }[]) =>
      uploadRef.current(items.map((i) => new window.File([i.data as BlobPart], i.name, { type: i.type || "" }))));
  }, []);
  const dismissPending = (key: string) => setPending((p) => p.filter((c) => c.key !== key));
  const onDrag = (e: React.DragEvent, kind: "enter" | "over" | "leave" | "drop") => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    if (kind === "enter") { dragDepth.current++; setDragging(true); }
    if (kind === "leave" && --dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); }
    if (kind === "drop") { dragDepth.current = 0; setDragging(false); upload(e.dataTransfer.files); }
  };

  // the preview pane opens the selected file through a short-lived private link
  const selected = files.find((f) => f.id === sel) || null;
  useEffect(() => {
    setViewUrl(null);
    if (!selected || previewKind(selected.type, selected.name) === "none") return;
    let live = true;
    mediaLink(token(), selected.id).then((l) => live && setViewUrl(l.url)).catch(() => live && setViewUrl(null));
    return () => { live = false; };
  }, [sel]);
  useEffect(() => { setSure(false); }, [sel]);
  const download = async () => {
    if (!selected) return;
    try { const l = await mediaLink(token(), selected.id, { download: true }); window.location.href = l.url; } catch (e) { say("Couldn't start the download. Try again."); }
  };
  const rename = async (newName: string) => {
    if (!selected || !newName.trim() || newName.trim() === selected.name) return;
    const before = selected.name;
    setFiles((all) => all.map((x) => (x.id === selected.id ? { ...x, name: newName.trim() } : x)));
    try { await renameMedia(token(), selected.id, newName.trim()); say("Renamed"); }
    catch (e) { setFiles((all) => all.map((x) => (x.id === selected.id ? { ...x, name: before } : x))); say(e instanceof Error ? e.message : "Couldn't rename it."); }
  };
  const remove = async () => {
    if (!selected) return;
    if (!sure) { setSure(true); setTimeout(() => alive.current && setSure(false), 3500); return; }
    const gone = selected;
    setSel(null); setFiles((all) => all.filter((x) => x.id !== gone.id));
    try { await deleteMedia(token(), gone.id); say("Deleted"); }
    catch (e) { setFiles((all) => [gone, ...all]); say(e instanceof Error ? e.message : "Couldn't delete it."); }
  };
  const copyLink = async () => {
    if (!selected) return;
    try {
      const l = await mediaLink(token(), selected.id, { days: 7 });
      try { await navigator.clipboard.writeText(l.url); say(l.seconds ? "Link copied. It works for 7 days." : "Link copied."); }
      catch (e) { window.prompt("Copy this link (it works for 7 days):", l.url); }
    } catch (e) { say(e instanceof Error ? e.message : "Couldn't make a link."); }
  };

  // SM-10: the public link switch. The server makes the link; off stops it at once.
  // Kyle, Oct 30: the switch now flips the moment it's clicked (the server takes a second or two)
  // and says what it's doing; Copy confirms on the button itself.
  const [publicBusy, setPublicBusy] = useState<"" | "on" | "off">("");
  const [copied, setCopied] = useState(false);
  const togglePublic = async () => {
    if (!selected || publicBusy) return;
    const id = selected.id;
    const on = !selected.publicId;
    setPublicBusy(on ? "on" : "off");
    try {
      const f = await setPublic(token(), id, on);
      setFiles((all) => all.map((x) => (x.id === id ? { ...x, publicId: f.publicId || null, namedLink: f.namedLink || null } : x)));
    } catch (e) { say(e instanceof Error ? e.message : "Couldn't change the public link. Try again."); }
    finally { if (alive.current) setPublicBusy(""); }
  };
  const copyPublicLink = async () => {
    if (!selected?.publicId) return;
    try {
      await navigator.clipboard.writeText(publicUrlOf(selected.publicId));
      setCopied(true);
      setTimeout(() => alive.current && setCopied(false), 2200);
    } catch (e) { say("Select the link and copy it."); }
  };
  const publicShown = publicBusy ? publicBusy === "on" : !!selected?.publicId;

  // OD-3: a short link on one of the account's domains, for a public file. The name starts as a
  // suggestion from the file name; the domains load once, the first time a public file is opened.
  const [hosts, setHosts] = useState<string[] | null>(null);
  const [linkHost, setLinkHost] = useState("");
  const [linkName, setLinkName] = useState("");
  const [linkErr, setLinkErr] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  useEffect(() => {
    if (!selected?.publicId || hosts) return;
    linkHosts(token()).then((h) => { if (alive.current) setHosts(h.hosts || []); }).catch(() => { if (alive.current) setHosts([]); });
  }, [selected?.publicId]);
  useEffect(() => {
    setLinkErr("");
    setLinkName(selected?.namedLink?.name || suggestLinkName(selected?.name || ""));
    setLinkHost(selected?.namedLink?.host || (hosts && hosts[0]) || "");
  }, [selected?.id, selected?.namedLink?.url, hosts]);
  const saveNamedLink = async () => {
    if (!selected || linkBusy) return;
    const id = selected.id;
    setLinkBusy(true); setLinkErr("");
    try {
      const f = await setNamedLink(token(), id, { host: linkHost, name: linkName.trim() });
      setFiles((all) => all.map((x) => (x.id === id ? { ...x, namedLink: f.namedLink || null } : x)));
      if (f.note) say(f.note);
    } catch (e) { if (alive.current) setLinkErr(e instanceof Error ? e.message : "Couldn't save the link. Try again."); }
    finally { if (alive.current) setLinkBusy(false); }
  };
  const dropNamedLink = async () => {
    if (!selected || linkBusy) return;
    const id = selected.id;
    setLinkBusy(true); setLinkErr("");
    try {
      await removeNamedLink(token(), id);
      setFiles((all) => all.map((x) => (x.id === id ? { ...x, namedLink: null } : x)));
    } catch (e) { if (alive.current) setLinkErr(e instanceof Error ? e.message : "Couldn't remove the link. Try again."); }
    finally { if (alive.current) setLinkBusy(false); }
  };

  // VH-4: while a public video is being prepared, check back every few seconds; stop once it's ready
  // or failed, or when another file is opened
  const videoState = selected?.publicId ? selected.video?.state : undefined;
  useEffect(() => {
    if (!selected || videoState !== "processing") return;
    const id = selected.id;
    const timer = setTimeout(async () => {
      try {
        const f = await getMedia(token(), id);
        if (alive.current) setFiles((all) => all.map((x) => (x.id === id ? { ...x, video: f.video || null } : x)));
      } catch (e) { /* the next check tries again */ }
    }, videoPollMs);
    return () => clearTimeout(timer);
  }, [selected?.id, videoState, files]);
  const tryVideoAgain = async () => {
    if (!selected) return;
    const id = selected.id;
    try {
      const f = await retryVideo(token(), id);
      setFiles((all) => all.map((x) => (x.id === id ? { ...x, video: f.video || null } : x)));
    } catch (e) { say(e instanceof Error ? e.message : "Couldn't start it again. Try again in a minute."); }
  };
  // VH-5: the player's settings for a ready video. Each change shows in the preview at once (the
  // preview passes the settings along) and is saved; color waits until the picker settles.
  const [player, setPlayer] = useState<PlayerSettings | null>(null);
  const colorTimer = useRef<any>(null);
  useEffect(() => {
    setPlayer(null);
    if (!selected || !selected.publicId || videoState !== "ready") return;
    const id = selected.id;
    playerSettings(token(), id).then((p) => { if (alive.current) setPlayer(p); }).catch(() => {});
  }, [selected?.id, videoState]);
  const changePlayer = (change: Partial<PlayerSettings> & { posterFileId?: string }, wait = 0) => {
    if (!selected || !player) return;
    const id = selected.id;
    const { posterFileId, ...shown } = change;
    setPlayer({ ...player, ...shown });
    const save = async () => {
      try {
        const p = await savePlayerSettings(token(), id, change);
        if (alive.current) setPlayer((cur) => (cur ? { ...cur, ...p } : p));
      } catch (e) { say(e instanceof Error ? e.message : "Couldn't save the player setting. Try again."); }
    };
    clearTimeout(colorTimer.current);
    if (wait) colorTimer.current = setTimeout(save, wait); else save();
  };
  const previewUrl = (pid: string) => {
    if (!player) return playUrlOf(pid);
    const { title, ...shown } = player;
    return `${playUrlOf(pid)}?s=${encodeURIComponent(JSON.stringify(shown))}`;
  };
  const posterChoices = files.filter((f) => f.type === "image" && String(f.url).startsWith("launchos-media:"));
  const [copiedWhat, setCopiedWhat] = useState("");
  const copyText = async (what: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedWhat(what);
      setTimeout(() => alive.current && setCopiedWhat(""), 2200);
    } catch (e) { say("Select the text and copy it."); }
  };

  // tags change on screen immediately; the server catches up behind the scenes (undone if it says no)
  const setTagsOn = (id: string, tags: string[]) => setFiles((all) => all.map((x) => (x.id === id ? { ...x, tags } : x)));
  const changeTag = async (change: { add: string } | { remove: string }) => {
    if (!selected) return;
    const before = selected.tags;
    if ("add" in change) {
      const known = allTags.find((t) => t.name.toLowerCase() === change.add.toLowerCase());
      const nameToAdd = known ? known.name : change.add;
      if (before.some((t) => t.toLowerCase() === nameToAdd.toLowerCase())) return;
      setTagsOn(selected.id, [...before, nameToAdd]);
      setAllTags((all) => (known ? all.map((t) => (t.name === known.name ? { ...t, count: t.count + 1 } : t)) : [...all, { name: nameToAdd, count: 1 }].sort((a, b) => a.name.localeCompare(b.name))));
    } else {
      setTagsOn(selected.id, before.filter((t) => t !== change.remove));
      setAllTags((all) => all.map((t) => (t.name === change.remove ? { ...t, count: Math.max(0, t.count - 1) } : t)));
    }
    try { const f = await tagFile(token(), selected.id, change); setTagsOn(f.id, f.tags); }
    catch (e) { setTagsOn(selected.id, before); say(e instanceof Error ? e.message : "Couldn't change the tag."); listTags(token()).then(setAllTags).catch(() => {}); }
  };
  const addTagToList = async () => {
    const tagName = newTag.trim(); if (!tagName) return;
    setNewTag("");
    if (!allTags.some((t) => t.name.toLowerCase() === tagName.toLowerCase())) setAllTags((all) => [...all, { name: tagName, count: 0 }].sort((a, b) => a.name.localeCompare(b.name)));
    try { await createTag(token(), tagName); } catch (e) { say(e instanceof Error ? e.message : "Couldn't add the tag."); listTags(token()).then(setAllTags).catch(() => {}); }
  };
  const saveTagRename = async () => {
    if (!editTag) return; const { from, text } = editTag; setEditTag(null);
    const to = text.trim(); if (!to || to === from) return;
    setAllTags((all) => all.map((t) => (t.name === from ? { ...t, name: to } : t)));
    setFiles((all) => all.map((x) => ({ ...x, tags: x.tags.map((t) => (t === from ? to : t)) })));
    if (tag === from) setTag(to);
    try { await renameTag(token(), from, to); } catch (e) { say(e instanceof Error ? e.message : "Couldn't rename the tag."); load(true); }
  };
  const removeTagEverywhere = async (tagName: string) => {
    if (sureTag !== tagName) { setSureTag(tagName); setTimeout(() => alive.current && setSureTag(null), 3500); return; }
    setSureTag(null);
    setAllTags((all) => all.filter((t) => t.name !== tagName));
    setFiles((all) => all.map((x) => ({ ...x, tags: x.tags.filter((t) => t !== tagName) })));
    if (tag === tagName) setTag(null);
    try { await deleteTag(token(), tagName); say(`Deleted the tag "${tagName}" (the files stay)`); } catch (e) { say(e instanceof Error ? e.message : "Couldn't delete the tag."); load(true); }
  };
  // take a file out of a campaign (files that are on a campaign's page stay part of it)
  const leaveCampaign = async (id: string) => {
    if (!selected) return;
    const before = selected.campaigns || [];
    setFiles((all) => all.map((x) => (x.id === selected.id ? { ...x, campaigns: before.filter((c) => c.id !== id || c.via === "page") } : x)));
    try { const f = await fileUnder(token(), selected.id, { remove: id }); setFiles((all) => all.map((x) => (x.id === f.id ? { ...x, campaigns: f.campaigns } : x))); }
    catch (e) { setFiles((all) => all.map((x) => (x.id === selected.id ? { ...x, campaigns: before } : x))); say(e instanceof Error ? e.message : "Couldn't change the campaign."); }
  };

  const toggleSide = () => setSideOpen((v) => { remember.set("lm-side", v ? "closed" : "open"); return !v; });
  const changeView = (v: "grid" | "list") => { setView(v); remember.set("lm-view", v); };
  const { files: shown, counts } = filterFiles(files, { type, tag, q, campaign });
  const shownPending = pending.filter((c) => !type || c.type === type);
  const campaigns = campaignList(files);
  const campaignName = campaign ? campaigns.find((c) => c.id === campaign)?.name || "This campaign" : null;
  const typeLabel = type ? MEDIA_TYPES.find((t) => t.key === type)?.label : "All files";

  const rows: DefaultDataProps[] = shown.map((f) => ({
    id: f.id,
    label: f.name,
    icon: <div className="w-7 h-7 rounded-lg overflow-hidden flex-none mr-2 relative"><TypeTile type={f.type} size={15} /></div>,
    data: [
      { id: "type", label: "Type", fieldType: "string", value: (MEDIA_TYPES.find((t) => t.key === f.type)?.label || "Other").replace(/s$/, "") },
      { id: "added", label: "Added", fieldType: "string", value: addedLabel(f.addedAt) },
    ],
  }));

  const filterButton = (key: MediaType | null, label: string, n: number | undefined) => {
    const Icon = key ? TYPE_ICON[key] : SquaresFour;
    return (
      <button key={label} type="button" className="lm-row" onClick={() => setType(key)} aria-pressed={type === key}>
        <Icon size={16} color={key ? TYPE_COLOR[key] : undefined} /><span className="t">{label}</span><span className="n">{n ?? ""}</span>
      </button>
    );
  };

  return (
    <Page
      topBar={{
        page: [{ label: name }],
        items: (
          <div className="flex items-center gap-2">
            <input ref={picker} type="file" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            <Button label="Upload" type={ButtonTypes.OUTLINED} onClick={() => picker.current?.click()} />
            <div className="lm-seg" role="group" aria-label="Show as">
              <button type="button" className="lm-iconbtn" onClick={() => changeView("grid")} aria-pressed={view === "grid"} title="Grid" aria-label="Grid"><SquaresFour size={18} /></button>
              <button type="button" className="lm-iconbtn" onClick={() => changeView("list")} aria-pressed={view === "list"} title="List" aria-label="List"><ListBullets size={18} /></button>
            </div>
          </div>
        ),
      }}
    >
      <div className="lm-lib">
        {sideOpen && (
          <aside className="lm-side" aria-label="Filters">
            <div className="lm-label">File type</div>
            {filterButton(null, "All files", counts?.all)}
            {MEDIA_TYPES.map((t) => filterButton(t.key, t.label, counts?.[t.key]))}
            {campaigns.length > 0 && <div className="lm-label" title="Every campaign is a tag automatically">Campaigns</div>}
            {campaigns.map((c) => (
              <button key={c.id} type="button" className="lm-row camp" onClick={() => setCampaign(campaign === c.id ? null : c.id)} aria-pressed={campaign === c.id} title={`${c.name} (follows the campaign's name)`}>
                <LinkSimple size={15} color="#0d9488" /><span className="t">{c.name}</span><span className="n">{c.count}</span>
              </button>
            ))}
            <div className="lm-label" title="Pencil renames a tag everywhere; the bin deletes it (files stay)">Tags</div>
            {allTags.map((t) => (
              <div key={t.name} className="lm-tagrow">
                {editTag?.from === t.name ? (
                  <input autoFocus value={editTag.text} onChange={(e) => setEditTag({ from: t.name, text: e.target.value })} onBlur={saveTagRename}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditTag(null); }}
                    aria-label={`Rename the tag ${t.name}`} className="flex-1 min-w-0 rounded-md border border-blue-500 bg-transparent px-2 py-1 text-sm" />
                ) : (
                  <button type="button" className="lm-row" onClick={() => setTag(tag === t.name ? null : t.name)} aria-pressed={tag === t.name}>
                    <TagIcon size={15} style={{ opacity: 0.5 }} /><span className="t">{t.name}</span><span className="n">{t.count}</span>
                  </button>
                )}
                {editTag?.from !== t.name && (
                  <span className={`acts ${sureTag === t.name ? "sure" : ""}`}>
                    <button type="button" onClick={() => setEditTag({ from: t.name, text: t.name })} aria-label={`Rename the tag ${t.name}`} title="Rename this tag everywhere"><PencilSimple size={13} /></button>
                    <button type="button" className={sureTag === t.name ? "sure" : ""} onClick={() => removeTagEverywhere(t.name)} aria-label={sureTag === t.name ? `Tap again to delete the tag ${t.name}` : `Delete the tag ${t.name}`} title={sureTag === t.name ? "Tap again to delete (files stay)" : "Delete this tag (files stay)"}><Trash size={13} /></button>
                  </span>
                )}
              </div>
            ))}
            <form className="flex gap-1.5 px-1 pt-1" onSubmit={(e) => { e.preventDefault(); addTagToList(); }}>
              <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="New tag" aria-label="New tag name" className="flex-1 min-w-0 rounded-lg border border-black/10 dark:border-white/20 bg-transparent px-2 py-1 text-sm" />
              <button type="submit" className="rounded-lg border border-black/10 dark:border-white/20 px-2 text-sm font-semibold">Add</button>
            </form>
          </aside>
        )}
        <div className="lm-main" onDragEnter={(e) => onDrag(e, "enter")} onDragOver={(e) => onDrag(e, "over")} onDragLeave={(e) => onDrag(e, "leave")} onDrop={(e) => onDrag(e, "drop")}>
          {dragging && <div className="lm-drop"><span className="flex items-center gap-2"><UploadSimple size={20} /> Drop to add to your library</span></div>}
          <div className="lm-tools">
            <button type="button" className="lm-iconbtn" onClick={toggleSide} aria-pressed={sideOpen} aria-label={sideOpen ? "Hide filters" : "Show filters"} title={sideOpen ? "Hide filters" : "Show filters"}><SidebarSimple size={18} /></button>
            <label className="lm-search">
              <MagnifyingGlass size={15} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files and tags" aria-label="Search files and tags" />
            </label>
            {campaign && <span className="lm-chip camp"><LinkSimple size={11} />{campaignName}<button type="button" onClick={() => setCampaign(null)} aria-label="Remove the campaign filter"><X size={11} /></button></span>}
            {tag && <span className="lm-chip">{tag}<button type="button" onClick={() => setTag(null)} aria-label="Remove the tag filter"><X size={11} /></button></span>}
          </div>
          {isLoading && <Loading type={view === "grid" ? "gallery" : "list"} />}
          {!isLoading && error && (
            <EmptyState title="Couldn't load your files" description={error} showImage={false}>
              <Button label="Try again" type={ButtonTypes.OUTLINED} onClick={() => load()} />
            </EmptyState>
          )}
          {!isLoading && !error && !files.length && !pending.length && (
            <EmptyState title="No files yet" description="Drag pictures, videos, audio or documents here, or use Upload. They stay private to your account." showImage={false} />
          )}
          {!isLoading && !error && (files.length > 0 || pending.length > 0) && (
            <div className="flex flex-col gap-3">
              <div className="flex items-baseline gap-2"><h2 className="text-sm font-semibold">{typeLabel}</h2><span className="text-xs text-black/40 dark:text-white/40">{shown.length} file{shown.length === 1 ? "" : "s"}</span></div>
              {!shown.length && !shownPending.length && <p className="text-sm text-black/50 dark:text-white/50">{q || tag || campaign ? "No files match these filters." : `No ${typeLabel?.toLowerCase()} yet.`}</p>}
              {(view === "grid" || shownPending.length > 0) && (shown.length > 0 || shownPending.length > 0) && (
                <div className="lm-grid">
                  {shownPending.map((c) => (
                    <div key={c.key} className={`lm-card pending ${c.error ? "failed" : ""}`} aria-label={`Uploading ${c.name}`}>
                      <div className="lm-thumb">
                        <Thumb f={{ id: c.key, name: c.name, type: c.type, url: "", tags: [], addedAt: "" }} local={c.local} />
                        {!c.error && <div className="bar"><i style={{ width: `${c.pct}%` }} /></div>}
                      </div>
                      <div className="lm-meta">
                        <p className="text-sm font-semibold" title={c.name}>{c.name}</p>
                        <p className="sub text-xs text-black/40 dark:text-white/40">{c.error ? <>{c.error} <button type="button" className="underline" onClick={() => dismissPending(c.key)}>Dismiss</button></> : `Uploading… ${c.pct}%`}</p>
                      </div>
                    </div>
                  ))}
                  {view === "grid" && shown.map((f) => (
                    <button type="button" key={f.id} className="lm-card" onClick={() => setSel(f.id)} aria-pressed={sel === f.id}>
                      <div className="lm-thumb"><Thumb f={f} />{(f.type === "video" || f.type === "pdf") && <span className="badge">{f.name.split(".").pop()}</span>}</div>
                      <div className="lm-meta">
                        <p className="text-sm font-semibold" title={f.name}>{f.name}</p>
                        <p className="text-xs text-black/40 dark:text-white/40">{addedLabel(f.addedAt)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {view === "list" && rows.length > 0 && <TableView items={rows} showFooterPagination={false} onItemClick={(id: string | number) => setSel(String(id))} />}
            </div>
          )}
        </div>
        {selected && (
          <aside className="lm-pane" aria-label="Preview">
            <div className="flex items-center gap-2 px-4 pt-4 pb-3">
              <input
                key={selected.id + selected.name}
                defaultValue={selected.name}
                aria-label="File name (click to rename)"
                title="Click to rename"
                onBlur={(e) => rename(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { (e.target as HTMLInputElement).value = selected.name; (e.target as HTMLInputElement).blur(); } }}
                className="flex-1 min-w-0 text-base font-semibold bg-transparent rounded-md px-1.5 py-1 border border-transparent hover:border-black/10 dark:hover:border-white/20 focus:border-blue-500 focus:outline-none"
              />
              <button type="button" onClick={() => setSel(null)} aria-label="Close preview" className="lm-iconbtn" style={{ border: 0 }}><X size={16} /></button>
            </div>
            <div className="mx-4 rounded-xl overflow-hidden border border-black/10 dark:border-white/10">
              <FilePreview type={selected.type} name={selected.name} url={isPublic(selected) ? selected.url : viewUrl} onDownload={download} />
              <div className="px-3 py-1.5 text-xs text-black/40 dark:text-white/40 border-t border-black/10 dark:border-white/10">Viewer: {viewerLabel[previewKind(selected.type, selected.name)]}</div>
            </div>
            <div className="lm-sec">
              <div className="lm-label" style={{ padding: 0 }}>Campaigns</div>
              {(selected.campaigns || []).length ? (
                <div className="flex flex-wrap gap-1.5">
                  {(selected.campaigns || []).map((c) => (
                    <span key={c.id} className="lm-chip camp" title={c.via === "page" ? "Used on one of this campaign's pages" : "Follows the campaign's name"}>
                      <LinkSimple size={11} />{c.name}
                      {c.via === "page" ? <span className="font-normal opacity-70">· on a page</span> : <button type="button" onClick={() => leaveCampaign(c.id)} aria-label={`Take out of ${c.name}`} className="opacity-60 hover:opacity-100"><X size={11} /></button>}
                    </span>
                  ))}
                </div>
              ) : <p className="text-xs text-black/40 dark:text-white/40">Not part of a campaign.</p>}
            </div>
            <div className="lm-sec">
              <div className="lm-label" style={{ padding: 0 }}>Tags</div>
              <TagPicker tags={selected.tags} all={allTags} onAdd={(n) => changeTag({ add: n })} onRemove={(n) => changeTag({ remove: n })} />
            </div>
            <div className="lm-sec">
              <div className="lm-lib-pubhead">
                <div className="lm-label" style={{ padding: 0 }}>Public link</div>
                <span className={`lm-lib-pubstate${publicShown ? " on" : ""}`} aria-live="polite">{publicBusy === "on" ? "Turning on…" : publicBusy === "off" ? "Turning off…" : publicShown ? "On" : "Off"}</span>
                <button type="button" role="switch" aria-checked={publicShown} aria-label="Public link" aria-busy={!!publicBusy} onClick={togglePublic} className={`lm-lib-switch${publicBusy ? " busy" : ""}`}>
                  <span />
                </button>
              </div>
              {selected.publicId && !publicBusy ? (
                <>
                  <div className="lm-lib-publink">
                    <input readOnly aria-label="Public link address" value={publicUrlOf(selected.publicId)} onFocus={(e) => e.target.select()} />
                    <button type="button" onClick={copyPublicLink} aria-label="Copy public link" className={copied ? "copied" : ""}>{copied ? "Copied ✓" : "Copy"}</button>
                  </div>
                  <p className="text-xs text-black/50 dark:text-white/50">Anyone with this link can open the file. It doesn't expire. Use it on a page or site. Turn it off to stop it.</p>
                  {hosts && hosts.length ? (
                    <div className="lm-lib-own" role="group" aria-label="Your own domain">
                      <div className="lm-lib-ownhead">Link on your own domain</div>
                      {selected.namedLink ? (
                        <>
                          <div className="lm-lib-publink">
                            <input readOnly aria-label="Link on your own domain" value={selected.namedLink.url} onFocus={(e) => e.target.select()} />
                            <button type="button" onClick={() => copyText("own", selected.namedLink!.url)} aria-label="Copy your own link" className={copiedWhat === "own" ? "copied" : ""}>{copiedWhat === "own" ? "Copied ✓" : "Copy"}</button>
                          </div>
                          <button type="button" className="lm-lib-ownremove" onClick={dropNamedLink} disabled={linkBusy}>Remove</button>
                        </>
                      ) : (
                        <>
                          <div className="lm-lib-ownrow">
                            <select aria-label="Domain" value={linkHost} onChange={(e) => setLinkHost(e.target.value)}>
                              {hosts.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                            <span aria-hidden="true">/media/</span>
                            <input aria-label="Link name" value={linkName} onChange={(e) => setLinkName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveNamedLink(); }} spellCheck={false} />
                          </div>
                          <button type="button" className="lm-lib-ownsave" onClick={saveNamedLink} disabled={linkBusy || !linkName.trim()}>{linkBusy ? "Saving…" : "Save link"}</button>
                        </>
                      )}
                      {linkErr ? <p className="lm-lib-ownerr" role="alert">{linkErr}</p> : <p className="text-xs text-black/50 dark:text-white/50">A short link on your own website. It stops when you turn the public link off.</p>}
                    </div>
                  ) : null}
                </>
              ) : publicBusy === "on" ? <p className="text-xs text-black/50 dark:text-white/50">Making the link…</p>
                : <p className="text-xs text-black/40 dark:text-white/40">Off. Only people on this account can open the file.</p>}
            </div>
            {selected.publicId && !publicBusy && selected.video ? (
              <div className="lm-sec">
                <div className="lm-label" style={{ padding: 0 }}>Video player</div>
                {selected.video.state === "processing" ? (
                  <div className="lm-lib-vstate" role="status">
                    <span className="lm-lib-vspin" aria-hidden="true" />
                    <div><strong>Getting your video ready…</strong><p>We're making versions for phones, slow connections and big screens. It usually takes a few minutes. You can leave this page.</p></div>
                  </div>
                ) : selected.video.state === "failed" ? (
                  <div className="lm-lib-vstate failed" role="alert">
                    <div><strong>We couldn't prepare this video.</strong><p>The public link above still works. Try again, and if it fails twice, the file may not be a video we can read.</p></div>
                    <button type="button" className="lm-lib-vretry" onClick={tryVideoAgain}>Try again</button>
                  </div>
                ) : (
                  <>
                    <div className="lm-lib-vframe"><iframe title="Video preview" src={previewUrl(selected.publicId)} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen /></div>
                    {player ? (
                      <div className="lm-lib-vset" aria-label="Player settings" role="group">
                        <label className="lm-lib-vrow">
                          <span>Player color</span>
                          <input type="color" aria-label="Player color" value={player.color.length === 4 ? `#${player.color.slice(1).split("").map((c) => c + c).join("")}` : player.color} onChange={(e) => changePlayer({ color: e.target.value }, 400)} />
                        </label>
                        {([["playButton", "Big play button"], ["controls", "Control bar"], ["autoplay", "Autoplay (muted)"], ["loop", "Loop"]] as const).map(([k, label]) => (
                          <div className="lm-lib-vrow" key={k}>
                            <span>{label}</span>
                            <button type="button" role="switch" aria-label={label} aria-checked={!!player[k]} className="lm-lib-switch" onClick={() => changePlayer({ [k]: !player[k] } as Partial<PlayerSettings>)}><span /></button>
                          </div>
                        ))}
                        <label className="lm-lib-vrow">
                          <span>Poster</span>
                          <select aria-label="Poster" value={player.poster === "frame" ? "frame" : "custom"} onChange={(e) => (e.target.value === "frame" ? changePlayer({ poster: "frame" }) : changePlayer({ poster: "custom", posterFileId: e.target.value }))}>
                            <option value="frame">A frame from the video</option>
                            {player.poster === "custom" ? <option value="custom">Your picture</option> : null}
                            {posterChoices.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                          </select>
                        </label>
                      </div>
                    ) : null}
                  </>
                )}
                {selected.video.state !== "failed" ? (
                  <>
                    <div className="lm-lib-vfield">
                      <label htmlFor="lm-embed">Embed code</label>
                      <textarea id="lm-embed" readOnly rows={3} value={embedCodeOf(selected.publicId)} onFocus={(e) => e.target.select()} />
                      <button type="button" onClick={() => copyText("embed", embedCodeOf(selected.publicId!))} aria-label="Copy embed code" className={copiedWhat === "embed" ? "copied" : ""}>{copiedWhat === "embed" ? "Copied ✓" : "Copy embed code"}</button>
                    </div>
                    <div className="lm-lib-vfield">
                      <label htmlFor="lm-share">Share link</label>
                      <input id="lm-share" readOnly value={playUrlOf(selected.publicId)} onFocus={(e) => e.target.select()} />
                      <button type="button" onClick={() => copyText("share", playUrlOf(selected.publicId!))} aria-label="Copy share link" className={copiedWhat === "share" ? "copied" : ""}>{copiedWhat === "share" ? "Copied ✓" : "Copy share link"}</button>
                    </div>
                    <p className="text-xs text-black/50 dark:text-white/50">{selected.video.state === "ready" ? "Paste the embed code into any page or site. It plays the right quality for each viewer." : "You can paste the embed code now. Until the video is ready, it shows “Getting this video ready…” and then plays by itself."}</p>
                  </>
                ) : null}
              </div>
            ) : null}
            <dl className="lm-lib-dl">
              <dt>Type</dt><dd>{(MEDIA_TYPES.find((t) => t.key === selected.type)?.label || "Other").replace(/s$/, "")}</dd>
              <dt>Added</dt><dd>{addedLabel(selected.addedAt)}</dd>
              <dt>Stored</dt><dd>{isPublic(selected) ? "Public (older upload)" : selected.publicId ? "Private, with a public link" : "Private to your account"}</dd>
            </dl>
            <div className="mt-auto flex flex-wrap items-center gap-2 p-4 border-t border-black/10 dark:border-white/10" style={{ marginTop: "auto", paddingTop: 16 }}>
              <Button label="Download" type={ButtonTypes.OUTLINED} onClick={download} />
              <Button label="Copy link" type={ButtonTypes.OUTLINED} onClick={copyLink} />
              <button type="button" onClick={remove} className={`px-3 py-2 rounded-lg text-sm font-semibold ${sure ? "bg-red-600 text-white" : "text-red-600 dark:text-red-300"}`}>{sure ? "Tap again to delete" : "Delete"}</button>
              {note && <span className="w-full text-xs text-black/50 dark:text-white/50" role="status">{note}</span>}
            </div>
          </aside>
        )}
      </div>
    </Page>
  );
};

// kept for the dashboard placeholder widget until SM-7 replaces it
export const UploadItemButton = (props: any) => <Button label="+ Upload a File" type={ButtonTypes.OUTLINED} {...props} />;
